import React, { memo, useEffect, useMemo, useState } from 'react';
import {
    DemeterDataFrequency,
    DemeterFeatureType,
    DemeterPageElementType,
    DemeterPageType,
    DemeterTableDefinitionType,
    UnitOfMeasure,
} from '../../../Generated/Raven-Demeter';
import useYearlyBalancesApi from '../../Apis/Hooks/useYearlyBalancesApiHook';
import BarChartRaw from '../../Components/Charts/Bar/BarChartRaw';
import { IChartData } from '../../Components/Charts/ChartDefinitions';
import chartService from '../../Components/Charts/ChartService';
import ChartWrapper from '../../Components/Charts/ChartWrapper/ChartWrapper';
import { IRegionCommoditySelection } from '../../Components/Navigation/Hooks/useRegionCommodityNavigationHook';
import useTableDefinition from '../../Components/Navigation/Hooks/useTableDefinitionHook';
import useFeatureFlag from '../../Services/FeatureFlags/useFeatureFlagHook';
import useLanguage from '../../Services/Language/useLanguageHook';
import { TrackingEvent } from '../../Services/Tracking/TrackingService';
import { IWithPageElementProps } from '../DashboardCustomization/DashboardCustomizationDefinitions';
import type { BalanceType } from './BalanceTablePage';

export interface IBalanceTableChartProps extends IWithPageElementProps {
    regionCommoditySelection: IRegionCommoditySelection;
    unitOfMeasure?: UnitOfMeasure;
    balanceType: BalanceType;
    isPercent?: boolean;
    pageType?: DemeterPageType;
    testId?: string;
    track: TrackingEvent;
}

export const yearsToDisplay = 10;

const BalanceTableChart: React.FC<IBalanceTableChartProps> = (props: IBalanceTableChartProps) => {
    const [, translate] = useLanguage();

    const [tableDefinitionRegion, tableDefinitionCommodity] = useTableDefinition(
        DemeterTableDefinitionType.CommodityYearlyBalanceTable,
        props.regionCommoditySelection,
    );
    const yearBalancesData = useYearlyBalancesApi(props.regionCommoditySelection, yearsToDisplay, props.unitOfMeasure);
    const [chartData, setChartData] = useState<IChartData[]>([]);

    useEffect(() => {
        if (!yearBalancesData?.rows) {
            return;
        }

        const newChartData = yearBalancesData.rows
            .filter((row) => row[props.balanceType] && row[props.balanceType].asOfDate)
            .slice(0, yearsToDisplay)
            .map((row) => ({
                value: props.isPercent ? +(row[props.balanceType].yearOverYearChangePercent ?? 0) : +(row[props.balanceType].value ?? 0),
                asOfDate: new Date(row[props.balanceType].asOfDate!),
                isActualValue: row[props.balanceType].isActualValue ?? true,
            }))
            .reverse();

        setChartData(newChartData);
    }, [yearBalancesData, props.balanceType, props.isPercent]);

    const title = useMemo(
        () =>
            `${translate(tableDefinitionRegion?.displayName ?? '')} ${translate(tableDefinitionCommodity?.displayName!)} ${translate(
                props.balanceType,
                'title',
            )}`,
        [tableDefinitionRegion, tableDefinitionCommodity, props.balanceType],
    );

    const hasRestrictedDataSource = chartService.isRestrictedDataSource(yearBalancesData?.dataSourceTag);

    return (
        <ChartWrapper
            headerOptions={{
                isCsvDownloadAvailable: useFeatureFlag(DemeterFeatureType.DownloadsGeneral) && !hasRestrictedDataSource,
                isXlsxDownloadAvailable: useFeatureFlag(DemeterFeatureType.DownloadsGeneral) && !hasRestrictedDataSource,
                isInDashboardEditMode: props.isInDashboardEditMode,
                maximizeMinimizeButton: props.maximizeMinimizeButton,
                pageType: props.pageType,
                pageElementType: DemeterPageElementType.BalanceTableChart,
                handleMoreOptionsSelection: props.handleMoreOptionsSelection,
            }}
            name="BalanceTableChart"
            title={title}
            dataSourceTag={yearBalancesData?.dataSourceTag ?? ''}
            isLoading={!chartData || chartData.length < 1}
            maxWidthHeightRatio={props.maxWidthHeightRatio}
            testId={props.testId}
            dragIndicatorIcon={props.dragIndicatorIcon}
            track={{ eventCategory: props.track.eventCategory, eventAction: props.track.eventAction }}
        >
            <BarChartRaw
                barSeries={[
                    {
                        label: title,
                        data: chartData ?? [],
                    },
                ]}
                format={props.isPercent ? 'percent' : 'value'}
                dataFrequency={DemeterDataFrequency.Yearly}
                unitOfMeasure={props.isPercent ? UnitOfMeasure.Percent : yearBalancesData?.unitOfMeasure}
                displayDecimalPlacesMinimum={props.isPercent ? 2 : tableDefinitionCommodity?.displayDecimalPlacesMinimum ?? 0}
                displayDecimalPlacesMaximum={props.isPercent ? 2 : tableDefinitionCommodity?.displayDecimalPlacesMinimum ?? 0}
                disableTooltip={hasRestrictedDataSource}
            />
        </ChartWrapper>
    );
};

export default memo(BalanceTableChart);
